import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';
import FSelect2 from '@/components/FSelect2';
import FComponentsLib from '@freelog/components-lib';
import styles from './index.less';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import * as AHooks from 'ahooks';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Cascader } from 'antd';
import fMessage from '@/components/fMessage';
import FModal from '@/components/FModal';
import FAuthenticationModal from '@/components/FAuthenticationModal';
import FidPhotoUpload from '@/components/FIdPhotoUpload';
import FIcons from '@/components/FIcons';
import province_city from '@/assets/data/province_city.json';
import { useParams } from 'react-router-dom';

dayjs.extend(customParseFormat);

interface SettlementInfoProps {}

interface SettlementInfoData {
  financialAccountType: number;
  ownerName: string;
  region: string;
  entityType: string;
  realName: string;
  idNumber: string;
  idPeriod: string;
  businessLicense: string;
  businessName: string;
  businessAddress: string;
  businessPeriod: string;
  settlementMethod: string;
  accountType: string;
  bankAccount: string;
  bankName: string;
  bankRegion: string;
}


// 模拟已填写的结算信息数据
const initVal: SettlementInfoData = {
  financialAccountType: 0,
  ownerName: '',
  region:
  // '中国大陆'
  FI18n.i18nNext.t('payout_setting_region_input_option_chinamainland'),
  entityType: 'individuals',
  realName: '',
  idNumber: '',
  idPeriod: '',
  businessLicense: '已上传',
  businessName: '飞致网络（深圳）有限公司',
  businessAddress: '广东省深圳市南山区桃源街道众冠时代广场',
  businessPeriod: '2020-10-10 —— 2030-10-10',
  // 银行卡
  settlementMethod: FI18n.i18nNext.t('payout_setting_payoutmethod_input_option_debitcard'),
  accountType: '',
  bankAccount: '',
  bankName: '',
  bankRegion: '',
};

interface ModifyIdValidityFormData {
  idCardFront: string;
  idCardBack: string;
  realName: string;
  idNumber: string;
  idPeriodStart: dayjs.Dayjs | null;
  idPeriodEnd: dayjs.Dayjs | null;
  idType: 'short' | 'long';
}

const initModifyFormData: ModifyIdValidityFormData = {
  idCardFront: '',
  idCardBack: '',
  realName: '',
  idNumber: '',
  idPeriodStart: null,
  idPeriodEnd: null,
  idType: 'short',
};

interface BankCardChangeFormData {
  settlementMethod: 'bankCard';
  accountType: 'individuals';
  bankAccount: string;
  bankAccountError: string;
  bankRegion: string[] | null;
  bankRegionError: string;
}

const initBankCardFormData: BankCardChangeFormData = {
  settlementMethod: 'bankCard',
  accountType: 'individuals',
  bankAccount: '',
  bankAccountError: '',
  bankRegion: null,
  bankRegionError: '',
};

const BANK_ACCOUNT_MIN_LENGTH = 8;
const BANK_ACCOUNT_MAX_LENGTH = 32;
/** 为空校验标记：用于红框，不展示文案 */
const BANK_ACCOUNT_EMPTY_ERROR = '__empty__';

function getBankAccountValidationError(value: string): string {
  const trimmed = value.trim();
  // 为空时只显示红框，不展示错误文案
  if (trimmed === '') {
    return BANK_ACCOUNT_EMPTY_ERROR;
  }
  if (trimmed.length < BANK_ACCOUNT_MIN_LENGTH || trimmed.length > BANK_ACCOUNT_MAX_LENGTH) {
    return '银行账号长度为 8~32 个字符';
  }
  return '';
}

function SettlementInfo(_props: SettlementInfoProps) {
  const params = useParams<{ id: string }>();
  const [$settlementData, set$SettlementData, get$SettlementData] =
    FUtil.Hook.useGetState<SettlementInfoData>(initVal);
  const [$showAuthenticationModal, set$ShowAuthenticationModal] =
    FUtil.Hook.useGetState<boolean>(false);
  const [$authAfterSuccess, set$AuthAfterSuccess, get$AuthAfterSuccess] =
    FUtil.Hook.useGetState<'idValidity' | 'bankCard' | null>(null);
  const [$modifyIdValidityModalOpen, set$ModifyIdValidityModalOpen] =
    FUtil.Hook.useGetState<boolean>(false);
  const [$modifyBankCardModalOpen, set$ModifyBankCardModalOpen] =
    FUtil.Hook.useGetState<boolean>(false);
  const [$modifyFormData, set$ModifyFormData, get$ModifyFormData] =
    FUtil.Hook.useGetState<ModifyIdValidityFormData>(initModifyFormData);
  const [$bankCardFormData, set$BankCardFormData, get$BankCardFormData] =
    FUtil.Hook.useGetState<BankCardChangeFormData>(initBankCardFormData);

  // 彩色照片or彩色扫描件说明（info4）
  const rawIdPhotoHint4 = FI18n.i18nNext.t('payout_setting_entity_input_idphoto_info4');
  const idPhotoHintLine2 =
    rawIdPhotoHint4 === 'payout_setting_entity_input_idphoto_info4'
      ? '彩色照片or彩色扫描件，可添加「Freelog认证」相关水印。'
      : rawIdPhotoHint4;

  AHooks.useMount(async () => {
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        accountType: number;
        availableBalance: number;
        certName: string;
        certNo: string;
        createDate: string;
        currencyType: number;
        freezeBalance: number;
        id: string;
        ownerId: number;
        ownerName: string;
        ownerUserId: number;
        ownerUserName: string;
        status: number;
        totalBalance: number;
        certBeginDate: string;
        certEndDate: string;
      };
    } = await FServiceAPI.Payment.queryFinancialAccountInfo2({
      accountId: params.id!,
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }

    let ownerDisplayName = data.ownerName;
    // 节点账户面包屑显示节点标题，而非账户 ownerName
    if (data.accountType === 2 && data.ownerId) {
      const nodeRes = await FServiceAPI.Node.details({ nodeId: data.ownerId });
      if (nodeRes.ret === 0 && nodeRes.errCode === 0 && nodeRes.data) {
        ownerDisplayName = nodeRes.data.nodeTitle || nodeRes.data.nodeName || data.ownerName;
      }
    }

    set$SettlementData({
      ...get$SettlementData(),
      financialAccountType: data.accountType,
      ownerName: ownerDisplayName,
      region: FI18n.i18nNext.t('payout_setting_region_input_option_chinamainland'),
      entityType: 'individuals',
      realName: data.certName,
      idNumber: data.certNo,
      idPeriod: `${data.certBeginDate} - ${data.certEndDate ?? '长期'}`,
      businessLicense: '已上传',
      businessName: '飞致网络（深圳）有限公司',
      businessAddress: '广东省深圳市南山区桃源街道众冠时代广场',
      businessPeriod: `${data.certBeginDate} - ${data.certEndDate ?? '长期'}`,
    });

    const {
      ret: ret2,
      errCode: errCode2,
      msg: msg2,
      data: data2,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        accountId: string;
        bankName: string;
        cardNo: string;
        cardType: number;
        ownerUserId: number;
        provId: string;
        areaId: string;
      };
    } = await FServiceAPI.Payment.queryBindWithdrawCard({
      accountId: params.id!,
    });
    if (ret2 !== 0 || errCode2 !== 0) {
      fMessage(msg2, 'error');
      return;
    }

    if (!data2) {
      return;
    }

    const province = (province_city as any).find((item: any) => item.value === data2.provId);
    const city = (province as any)?.children?.find((item: any) => item.value === data2.areaId);
    set$SettlementData({
      ...get$SettlementData(),
      // 银行卡
      settlementMethod: FI18n.i18nNext.t('payout_setting_payoutmethod_input_option_debitcard'),
      // 个人账户
      accountType: FI18n.i18nNext.t(
        'payout_setting_payoutmethod_input_accounttype_option_individual',
      ),
      bankAccount: data2.cardNo,
      bankName: data2.bankName,
      bankRegion: `${province?.label ?? ''} / ${city?.label ?? ''}`,
    });
  });

  function onChangeBankAccount(value: string) {
    set$BankCardFormData({
      ...get$BankCardFormData(),
      bankAccount: value,
      bankAccountError: getBankAccountValidationError(value),
    });
  }

  function onChangeBankRegion(value: string[] | null) {
    let error = '';
    if (value === null) {
      error = '请选择开户银行所在地区';
    }
    set$BankCardFormData({
      ...get$BankCardFormData(),
      bankRegion: value,
      bankRegionError: error,
    });
  }

  async function reloadSettlementBankCardDisplay() {
    const {
      ret,
      errCode,
      msg,
      data: data2,
    } = await FServiceAPI.Payment.queryBindWithdrawCard({
      accountId: params.id!,
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }
    if (!data2) {
      return;
    }
    const province = (province_city as any).find((item: any) => item.value === data2.provId);
    const city = (province as any)?.children?.find((item: any) => item.value === data2.areaId);
    set$SettlementData({
      ...get$SettlementData(),
      // 银行卡
      settlementMethod: FI18n.i18nNext.t('payout_setting_payoutmethod_input_option_debitcard'),
      // 个人账户
      accountType: FI18n.i18nNext.t(
        'payout_setting_payoutmethod_input_accounttype_option_individual',
      ),
      bankAccount: data2.cardNo,
      bankName: data2.bankName,
      bankRegion: `${province?.label ?? ''} / ${city?.label ?? ''}`,
    });
  }

  return (
    <>
      <div className={styles.settlementInfoContainer}>
        <div className={styles.headerSection}>
          <h2 className={styles.title}>
            <span className={styles.titleName}>
              {$settlementData.financialAccountType === 1
                ? // 资源创作收入
                  '资源创作收入'
                : $settlementData.ownerName}
            </span>
            <span className={styles.titleDivider}>/</span>
            <span className={styles.titleCurrent}>
              {
                // 结算信息
                FI18n.i18nNext.tAuto('payout_setup_title')
              }
            </span>
          </h2>
        </div>

        {/* 所在区域 */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {
              // 所在区域
              FI18n.i18nNext.tAuto('payout_setting_region_input')
            }
          </div>
          <div className={styles.readOnlyField}>
            <div className={styles.readOnlyValue}>
              {$settlementData.region}
            </div>
          </div>
        </div>

        {/* 认证信息 */}
        <div className={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className={styles.sectionTitle}>
              {
                // 认证信息
                FI18n.i18nNext.tAuto('payout_setting_entity_title')
              }
            </div>
            <FComponentsLib.FTextBtn
              type='primary'
              style={{ fontSize: 12 }}
              onClick={() => {
                set$AuthAfterSuccess('idValidity');
                set$ShowAuthenticationModal(true);
              }}
            >
              {
                // 更新证件有效期
                FI18n.i18nNext.tAuto('payout_setting_entity_btn_updatevalidity')
              }
            </FComponentsLib.FTextBtn>
          </div>
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {
                // 主体类型
                FI18n.i18nNext.tAuto('payout_setting_entity_input_type')
              }
            </div>
            <div className={styles.readOnlyField}>
              <div className={styles.readOnlyValue}>
                {$settlementData.entityType === 'individuals'
                  ? FI18n.i18nNext.tAuto('payout_setting_entity_input_type_option_individual')
                  : $settlementData.entityType}
              </div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {
                // 真实姓名
                FI18n.i18nNext.tAuto('payout_setting_entity_input_name')
              }
            </div>
            <div className={styles.readOnlyField}>
              <div className={styles.readOnlyValue}>{$settlementData.realName}</div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {
                // 身份证号
                FI18n.i18nNext.tAuto('payout_setting_entity_input_idno')
              }
            </div>
            <div className={styles.readOnlyField}>
              <div className={styles.readOnlyValue}>{$settlementData.idNumber}</div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {
                // 身份证期限
                FI18n.i18nNext.tAuto('payout_setting_entity_input_idvalidation')
              }
            </div>
            <div className={styles.readOnlyField}>
              <div className={styles.readOnlyValue}>{$settlementData.idPeriod}</div>
            </div>
          </div>

          {$settlementData.entityType !== 'individuals' && (
            <>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>营业执照证件照片</div>
                <div className={styles.uploadStatusContainer}>
                  <div className={styles.uploadStatus}>已上传</div>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>营业执照主体名称</div>
                <div className={styles.readOnlyField}>
                  <div className={styles.readOnlyValue}>{$settlementData.businessName}</div>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>注册地址</div>
                <div className={styles.readOnlyField}>
                  <div className={styles.readOnlyValue}>{$settlementData.businessAddress}</div>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>营业期限</div>
                <div className={styles.readOnlyField}>
                  <div className={styles.readOnlyValue}>{$settlementData.businessPeriod}</div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 结算账号 */}
        <div className={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className={styles.sectionTitle}>
              {
                // 结算账号
                FI18n.i18nNext.tAuto('payout_setting_payoutmethod_title')
              }
            </div>
            <FComponentsLib.FTextBtn
              type='primary'
              style={{ fontSize: 12 }}
              onClick={() => {
                set$AuthAfterSuccess('bankCard');
                set$ShowAuthenticationModal(true);
              }}
            >
              {
                // 更换结算方式
                FI18n.i18nNext.tAuto('payout_setting_payoutmethod_btn_changepayoutmethod')
              }
            </FComponentsLib.FTextBtn>
          </div>
          <div className={styles.fieldGroup}>
            {/* <div className={styles.fieldLabel}>
              {
                // 结算方式
                FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input')
              }
            </div> */}
            <div className={styles.readOnlyField}>
              <div className={styles.readOnlyValue}>{$settlementData.settlementMethod}</div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {
                // 账户类型
                FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_accounttype')
              }
            </div>
            <div className={styles.readOnlyField}>
              <div className={styles.readOnlyValue}>{$settlementData.accountType}</div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {
                // 银行卡账号
                FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_bankaccount')
              }
            </div>
            <div className={styles.readOnlyField}>
              <div className={styles.readOnlyValue}>{$settlementData.bankAccount}</div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {
                // 开户银行
                FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_bankname')
              }
            </div>
            <div className={styles.readOnlyField}>
              <div className={styles.readOnlyValue}>{$settlementData.bankName}</div>
            </div>
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              {
                // 开户银行所在地区
                FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_bankregion')
              }
            </div>
            <div className={styles.readOnlyField}>
              <div className={styles.readOnlyValue}>{$settlementData.bankRegion}</div>
            </div>
          </div>
        </div>
      </div>

      <FModal
        title={null}
        closable={false}
        centered
        open={$modifyIdValidityModalOpen}
        onCancel={() => set$ModifyIdValidityModalOpen(false)}
        footer={null}
        destroyOnHidden
        width={700}
        styles={{
          body: { padding: 0 },
          content: { padding: 0, borderRadius: 4 },
        }}
      >
        <div className={styles.modifyIdValidityModal}>
          <div className={styles.modifyModalHeader}>
            <div className={styles.modifyModalHeaderSpacer} aria-hidden />
            <div className={styles.modifyModalTitleWrap}>
              <span className={styles.modifyModalTitle}>
                {
                  // 修改身份证期限
                  FI18n.i18nNext.tAuto('payout_setting_entity_title2')
                }
              </span>
            </div>
            <div className={styles.modifyModalHeaderClose}>
              <button
                type="button"
                className={styles.modifyModalCloseBtn}
                onClick={() => set$ModifyIdValidityModalOpen(false)}
              >
                <FIcons.FClose style={{ fontSize: 16, color: '#000' }} />
              </button>
            </div>
          </div>
          <div className={styles.modalIdPhotoSection}>
            <div className={styles.modalSectionLabel}>
              {
                // 更新身份证有效期弹窗内身份证照片区域标题
                FI18n.i18nNext.tAuto('payout_setting_entity_updatevalidity_msg')
              }
            </div>
            <div className={styles.modalIdPhotoUploadWrapper}>
              <FidPhotoUpload
              className={styles.modalFidPhotoRoot}
              onFrontSuccess={(data) => {
                set$ModifyFormData({
                  ...get$ModifyFormData(),
                  idCardFront: data.certImagePath,
                  realName: data.name,
                  idNumber: data.idNumber,
                });
              }}
              onBackSuccess={(data) => {
                const validPeriod = data.validPeriod;
                const [start, end] = validPeriod.split('-');
                const idPeriodStart = dayjs(start?.trim(), 'YYYY.MM.DD');
                const idPeriodEnd =
                  !end || end?.trim() === '长期'
                    ? null
                    : dayjs(end?.trim(), 'YYYY.MM.DD');
                const idType: 'long' | 'short' = !idPeriodEnd ? 'long' : 'short';
                set$ModifyFormData({
                  ...get$ModifyFormData(),
                  idCardBack: data.certImagePath,
                  idPeriodStart: get$ModifyFormData().idPeriodStart ?? idPeriodStart,
                  idPeriodEnd: get$ModifyFormData().idPeriodStart
                    ? get$ModifyFormData().idPeriodEnd
                    : idPeriodEnd,
                  idType: get$ModifyFormData().idPeriodStart
                    ? get$ModifyFormData().idType
                    : idType,
                });
              }}
              />
            </div>
            <div className={styles.modalPhotoHint}>
                {
                  // 正面拍摄、清晰、四角完整、无反光或遮挡；不得翻拍、截图、镜像、PS。
                  FI18n.i18nNext.tAuto('payout_setting_entity_input_idphoto_info3')
                }
            </div>
          </div>
          <div className={styles.modalFooter}>
            <FComponentsLib.FRectBtn
              type='default'
              onClick={() => set$ModifyIdValidityModalOpen(false)}
            >
              {
                // 取消
                FI18n.i18nNext.tAuto('btn_cancel')
              }
            </FComponentsLib.FRectBtn>
            <FComponentsLib.FRectBtn
              type='primary'
              disabled={
                !get$ModifyFormData().idCardFront ||
                !get$ModifyFormData().idCardBack ||
                !get$ModifyFormData().idPeriodStart
              }
              onClick={async () => {
                const {
                  idNumber,
                  realName,
                  idCardFront,
                  idCardBack,
                  idPeriodStart,
                  idPeriodEnd,
                  idType,
                } = get$ModifyFormData();
                const {
                  ret,
                  errCode,
                  msg,
                } = await FServiceAPI.Payment.updateFinancialAccount({
                  accountId: params.id!,
                  certNo: idNumber.trim(),
                  name: realName.trim(),
                  certValidityType: idType === 'long' ? 1 : 0,
                  certBeginDate:
                    idPeriodStart?.format(FUtil.Predefined.momentDateFormat) ?? '',
                  certEndDate: idPeriodEnd?.format(FUtil.Predefined.momentDateFormat) || undefined,
                  certImagePaths: [idCardFront, idCardBack],
                });
                if (ret !== 0 || errCode !== 0) {
                  fMessage(msg, 'error');
                  return;
                }
                fMessage('提交成功', 'success');
                set$ModifyIdValidityModalOpen(false);
                set$SettlementData({
                  ...get$SettlementData(),
                  idPeriod: `${idPeriodStart?.format('YYYY-MM-DD')} - ${idPeriodEnd ? idPeriodEnd.format('YYYY-MM-DD') : '长期'}`,
                });
              }}
            >
              {
                // 提交
                FI18n.i18nNext.tAuto('payout_setting_entity_btn_submit')
              }
            </FComponentsLib.FRectBtn>
          </div>
        </div>
      </FModal>

      <FModal
        title={null}
        closable={false}
        centered
        open={$modifyBankCardModalOpen}
        onCancel={() => set$ModifyBankCardModalOpen(false)}
        footer={null}
        destroyOnHidden
        width={820}
        styles={{
          body: { padding: 0 },
          content: { padding: 0, borderRadius: 4, minWidth: 460 },
        }}
      >
        <div className={styles.modifyBankCardModal}>
          <div className={styles.modifyModalHeader}>
            <div className={styles.modifyModalHeaderSpacer} aria-hidden />
            <div className={styles.modifyModalTitleWrap}>
              <span className={styles.modifyModalTitle}>
                {
                  // 更换结算方式（弹窗标题）
                  FI18n.i18nNext.tAuto('payout_setting_payoutmethod_title_changepayoutmethod')
                }
              </span>
            </div>
            <div className={styles.modifyModalHeaderClose}>
              <button
                type="button"
                className={styles.modifyModalCloseBtn}
                onClick={() => set$ModifyBankCardModalOpen(false)}
              >
                <FIcons.FClose style={{ fontSize: 16, color: '#000' }} />
              </button>
            </div>
          </div>
          <div className={styles.bankCardModalFields}>
            <div className={styles.bankCardModalField}>
              <div className={styles.fieldLabel}>
                {
                  // 结算方式
                  FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input')
                }
              </div>
              <div className={styles.selectorWrapper}>
                <FSelect2
                  value={$bankCardFormData.settlementMethod || undefined}
                  onChange={(value) => {
                    set$BankCardFormData({ ...get$BankCardFormData(), settlementMethod: value });
                  }}
                  className={styles.selector}
                  options={[
                    {
                      label:
                        // 银行卡
                        FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_option_debitcard'),
                      value: 'bankCard',
                    },
                  ]}
                />
              </div>
              <div className={styles.hintText}>
                {
                  // 目前仅提供银行卡的结算方式，其他方式后续开放...
                  FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_info')
                }
              </div>
            </div>

            <div className={styles.bankCardModalField}>
              <div className={styles.fieldLabel}>
                {
                  // 账户类型
                  FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_accounttype')
                }
              </div>
              <div className={styles.selectorWrapper}>
                <FSelect2
                  value={$bankCardFormData.accountType || undefined}
                  onChange={(value) => {
                    set$BankCardFormData({ ...get$BankCardFormData(), accountType: value });
                  }}
                  className={styles.selector}
                  options={[
                    {
                      label:
                        // 个人账户
                        FI18n.i18nNext.tAuto(
                          'payout_setting_payoutmethod_input_accounttype_option_individual',
                        ),
                      value: 'individuals',
                    },
                  ]}
                />
              </div>
            </div>

            <div className={`${styles.bankCardModalField} ${styles.bankCardModalFieldAccount}`}>
              <div className={styles.fieldLabel}>
                {
                  // 银行账号
                  FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_bankaccount')
                }
              </div>
              <FInput_PinyinSafeTextCounter
                lengthLimit={BANK_ACCOUNT_MAX_LENGTH}
                value={$bankCardFormData.bankAccount}
                hasError={$bankCardFormData.bankAccountError !== ''}
                onChangeValue={(value) => {
                  onChangeBankAccount(value.replace(/\s/g, ''));
                }}
                style={{ width: '100%' }}
                onBlur={() => {
                  onChangeBankAccount(get$BankCardFormData().bankAccount.trim());
                }}
              />
              {$bankCardFormData.bankAccountError !== '' &&
                $bankCardFormData.bankAccountError !== BANK_ACCOUNT_EMPTY_ERROR && (
                  <div className={styles.fieldError}>{$bankCardFormData.bankAccountError}</div>
                )}
            </div>

            <div className={styles.bankCardModalField}>
              <div className={styles.fieldLabel}>
                {
                  // 开户银行所在地区
                  FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_bankregion')
                }
              </div>
              <div className={styles.selectorWrapper}>
                <Cascader
                  value={$bankCardFormData.bankRegion ?? undefined}
                  onChange={(value) => {
                    onChangeBankRegion(value as string[]);
                  }}
                  onBlur={() => {
                    onChangeBankRegion(get$BankCardFormData().bankRegion);
                  }}
                  className={styles.selector}
                  options={province_city}
                  status={$bankCardFormData.bankRegionError ? 'error' : undefined}
                />
              </div>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <FComponentsLib.FRectBtn
              type='default'
              onClick={() => set$ModifyBankCardModalOpen(false)}
            >
              {
                // 取消
                FI18n.i18nNext.tAuto('btn_cancel')
              }
            </FComponentsLib.FRectBtn>
            <FComponentsLib.FRectBtn
              type='primary'
              disabled={
                $bankCardFormData.bankRegion === null ||
                $bankCardFormData.bankRegionError !== '' ||
                $bankCardFormData.bankAccount.trim() === '' ||
                $bankCardFormData.bankAccountError !== ''
              }
              onClick={async () => {
                const { bankRegion, bankAccount } = get$BankCardFormData();
                const {
                  ret: ret2,
                  errCode: errCode2,
                  msg: msg2,
                } = await FServiceAPI.Payment.bindWithdrawCard({
                  accountId: params.id!,
                  cardType: 1,
                  cardNo: bankAccount,
                  provId: bankRegion?.[0] ?? '',
                  areaId: bankRegion?.[1] ?? '',
                });
                if (ret2 !== 0 || errCode2 !== 0) {
                  fMessage(msg2, 'error');
                  return;
                }
                fMessage('提交成功', 'success');
                set$ModifyBankCardModalOpen(false);
                await reloadSettlementBankCardDisplay();
              }}
            >
              {
                // 提交
                FI18n.i18nNext.tAuto('payout_setting_payoutmethod_btn_submit')
              }
            </FComponentsLib.FRectBtn>
          </div>
        </div>
      </FModal>

      <FAuthenticationModal
        open={$showAuthenticationModal}
        onOk={() => {
          set$ShowAuthenticationModal(false);
          const intent = get$AuthAfterSuccess();
          set$AuthAfterSuccess(null);
          if (intent === 'idValidity') {
            set$ModifyFormData(initModifyFormData);
            set$ModifyIdValidityModalOpen(true);
          } else if (intent === 'bankCard') {
            set$BankCardFormData(initBankCardFormData);
            set$ModifyBankCardModalOpen(true);
          }
        }}
        onCancel={() => {
          set$ShowAuthenticationModal(false);
          set$AuthAfterSuccess(null);
        }}
      />
    </>
  );
}

export default SettlementInfo;
